import React, {
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Label from "./Label";
import "./css/ArrayField.css";

export default function ArrayField({
  id,
  value,
  name,
  onChange,
  className,
  children,
  createItem,
  isInvalid,
  labelAs,
  label,
  labelStyle,
  error,
  disabled,
  defaultIndex,
  onRemoveIndex,
  onAppendIndex,
  onReplaceIndex,
  onInsertIndex,
  ...props
}) {
  const inputRef = useRef();
  const [selectedIndex, setSelectedIndex] = useState(() => {
    if (!!defaultIndex) return defaultIndex;
    if (!value || value.length === 0) return -1;
    return 0;
  });
  const [selectedItem, setSelectedItem] = useState(
    !!createItem && createItem(value),
  );
  const handleChange = useMemo(
    () => (event) => {
      if (event.target === inputRef.current) {
        if (onChange instanceof Function) {
          onChange(event);
        }
      } else {
        event.stopPropagation();
      }
    },
    [onChange],
  );
  const insert = (item, index) => {
    const array = [
      ...((index > 0 && value.slice(0, index)) || []),
      item,
      ...((index < value.length - 1 && value.slice(index)) || []),
    ];
    inputRef.current.name = name;
    inputRef.current.value = array;
    inputRef.current.dispatchEvent(new Event("change"));
    if (onInsertIndex === "first") {
      select(0);
    } else if (onInsertIndex === "last") {
      select(array.length - 1);
    } else if (onInsertIndex === "current") {
      select(index);
    } else {
      select(-1);
    }
  };
  const replace = (item, index) => {
    const array = [
      ...((index > 0 && value.slice(0, index)) || []),
      item,
      ...((index + 1 <= value.length - 1 && value.slice(index + 1)) || []),
    ];
    inputRef.current.name = name;
    inputRef.current.value = array;
    inputRef.current.dispatchEvent(new Event("change"));
    if (onReplaceIndex === "first") {
      select(0);
    } else if (onReplaceIndex === "last") {
      select(array.length - 1);
    } else if (onReplaceIndex === "current") {
      select(selectedIndex);
    } else {
      select(-1);
    }
  };
  const append = (item) => {
    const array = [...value, item];
    inputRef.current.name = name;
    inputRef.current.value = array;
    inputRef.current.dispatchEvent(new Event("change"));
    if (onAppendIndex === "new") {
      select(0);
    } else if (onAppendIndex === "last" || onAppendIndex === "current") {
      select(array.length - 1);
    } else {
      select(-1);
    }
  };
  const remove = (index) => {
    const array = [
      ...((index > 0 && value.slice(0, index)) || []),
      ...((index < value.length - 1 && value.slice(index + 1)) || []),
    ];
    inputRef.current.name = name;
    inputRef.current.value = array;
    inputRef.current.dispatchEvent(
      new Event("change", { bubbles: true, cancelable: true }),
    );
    if (onRemoveIndex === "first") {
      select(0);
    } else if (onRemoveIndex === "last") {
      select(array.length - 1);
    } else if (onRemoveIndex === "current" && index === selectedIndex) {
      select(selectedIndex - 1);
    } else {
      select(-1);
    }
  };
  const select = (index) => {
    setSelectedIndex(index);
  };
  const computedClassName = [
    "array-field",
    "form-group",
    ...className.split(" "),
  ]
    .join(" ")
    .trim();
  useEffect(() => {
    const input = inputRef.current;
    const onChange = handleChange;
    if (!!input) {
      input.addEventListener("change", onChange);
      return () => {
        input.removeEventListener("change", onChange);
      };
    }
  }, [handleChange]);

  useEffect(() => {
    if (selectedIndex >= 0 && selectedIndex < value.length) {
      setSelectedItem(value[selectedIndex]);
    } else if (selectedIndex < 0 && createItem instanceof Function) {
      setSelectedItem(createItem(value));
    } else {
      setSelectedItem(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, createItem]);
  return (
    <div id={id} className={computedClassName} ref={inputRef}>
      {!!label &&
        React.cloneElement(
          labelAs,
          { className: "array-field-label", style: labelStyle },
          label,
        )}
      <div className="array-field-wrapper">
        {children instanceof Function &&
          children({
            value,
            insert,
            append,
            replace,
            remove,
            select,
            selectedIndex,
            selectedItem,
            setSelectedItem,
            disabled,
          })}
        {isValidElement(children) &&
          cloneElement(children, {
            value,
            insert,
            append,
            replace,
            remove,
            select,
            selectedIndex,
            selectedItem,
            setSelectedItem,
            disabled,
          })}
      </div>
      {isInvalid && !!error && (
        <div className="has-error">
          <span className="help-block">{error}</span>
        </div>
      )}
    </div>
  );
}

ArrayField.defaultProps = {
  className: "",
  value: [],
  labelAs: <Label />,
  onRemoveIndex: "current",
  onAppendIndex: "current",
  onReplaceIndex: "current",
  onInsertIndex: "current",
};
